import ThemeName from "@/types/styles/ThemeName";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faBan, faChalkboardUser, faChartColumn, faCheckCircle, faDatabase, faExclamationTriangle, faFileLines, faFileMedical, faGraduationCap, faInfo, faInfoCircle, faKitMedical, faNotesMedical, faPencil, faPerson, faPersonDress, faQuestionCircle, faStar, faTimesHexagon, faUser, faUserMagnifyingGlass, faStar as solidStar } from "@fortawesome/pro-duotone-svg-icons";
import { GetClientAppSettingsResponse, SentrySettings } from "./api/dtos";



export interface ISentrySettings {
    dsn?: string;
    customer: string;
    environment: string;
    sendDefaultPii: boolean;
    enableMetrics: boolean;
    attachStacktrace: boolean;
    debug: boolean;
    application: string;
    release?: string;
    replaysSessionSampleRate: number;
    replaysOnErrorSampleRate: number;
    enableLogs: boolean;
    tracesSampleRate: number;
    enableReportABug: boolean;
}
export interface IAppSettings {
    sentry: ISentrySettings;
};

const ClientAppSettingsResponse: GetClientAppSettingsResponse | undefined = (window as any).APP_SETTINGS;
const getSentryAppSettingValue = <TData>(
    appSettingProperty: keyof SentrySettings | undefined
): TData | undefined => {
    if (!appSettingProperty) {
        return undefined;
    }
    const value: TData | undefined = ClientAppSettingsResponse?.settings?.sentry?.[appSettingProperty] as TData;
    if (value === undefined || value === null) {
        return undefined;
    }
    return value;
};
const getSentryEnvironmentValue = (
    environmentProperty: string | undefined
): string | undefined => {
    if (!environmentProperty) {
        return undefined;
    }
    const value: string | undefined = import.meta.env[environmentProperty];
    if (value === undefined || value === null || value === "") {
        return undefined;
    }
    return value;
};
const getSentryEnvironmentString = (
    environmentProperty: string | undefined
): string | undefined => {
    return getSentryEnvironmentValue(environmentProperty);
};
const getSentryEnvironmentNumber = (
    environmentProperty: string | undefined
): number | undefined => {
    const valueStr = getSentryEnvironmentValue(environmentProperty);
    if (!valueStr) {
        return undefined;
    }
    try {
        return parseFloat(valueStr);
    }
    catch {
        return undefined;
    }
};
const getSentryEnvironmentBoolean = (
    environmentProperty: string | undefined
): boolean | undefined => {
    const valueStr = getSentryEnvironmentValue(environmentProperty);
    if (!valueStr) {
        return undefined;
    }
    const lowerCase = valueStr.toLowerCase();
    if ( lowerCase === "1" || lowerCase === 'true') {
        return true;
    }
    if (lowerCase === "0" || lowerCase === 'false') {
        return false;
    }
    return undefined;
};
const getSentryString = (
    appSettingProperty: keyof SentrySettings | undefined, 
    environmentProperty: string | undefined
) => {
    const appSettingValue = getSentryAppSettingValue<string>(appSettingProperty);
    if (appSettingValue) {
        return appSettingValue;
    }
    const environmentValue = getSentryEnvironmentString(environmentProperty);
    if (environmentValue) {
        return environmentValue;
    }
    return undefined;
};
const getSentryNumber = (
    appSettingProperty: keyof SentrySettings | undefined, 
    environmentProperty: string | undefined
) => {
    const appSettingValue = getSentryAppSettingValue<number>(appSettingProperty);
    if (appSettingValue !== null && appSettingValue !== undefined) {
        return appSettingValue;
    }
    const environmentValue = getSentryEnvironmentNumber(environmentProperty);
    if (environmentValue !== null && environmentValue !== undefined) {
        return environmentValue;
    }
    return undefined;
};
const getSentryBoolean = (
    appSettingProperty: keyof SentrySettings | undefined, 
    environmentProperty: string | undefined
) => {
    const appSettingValue = getSentryAppSettingValue<boolean>(appSettingProperty);
    if (appSettingValue !== null && appSettingValue !== undefined) {
        return appSettingValue;
    }
    const environmentValue = getSentryEnvironmentBoolean(environmentProperty);
    if (environmentValue !== null && environmentValue !== undefined) {
        return environmentValue;
    }
    return undefined;
};



const urlParams = new URLSearchParams(window.location.search)

let overrideTheme: ThemeName | undefined = undefined;

if (urlParams.has('theme')) {
    const theme = urlParams.get('theme');

    if (theme === 'dark' || theme === 'light') {
        overrideTheme = theme;
    }
}

export interface IPaletteSettings {
    primaryLight: string;
    secondaryLight: string;
    primaryDark: string;
    secondaryDark: string;
    GreenLight: string;
    GreenDark: string;
    OrangeLight: string;
    OrangeDark: string;
    RedLight: string;
    RedDark: string;
};

export interface IIconSettings {
    alertSuccess: IconProp;
    alertInformation: IconProp;
    alertWarning: IconProp;
    alertError: IconProp;
    alertDefault: IconProp;
    favourite: IconProp;
    notFavourite: IconProp;
    patients: IconProp;
    eCrf: IconProp;
    stats: IconProp;
    screening: IconProp;
    registration: IconProp;
    drugManagement: IconProp;
    saeReview: IconProp;
    piSignOff: IconProp;
    medicalReview: IconProp;
    trialManagement: IconProp;
    sourceDataVerification: IconProp;
    trialParticipants: IconProp;
    faq: IconProp;
    training: IconProp;
    staffTraining: IconProp;
    outstandingQueries: IconProp;
};

export interface IClientSettings {
    eventStreamUrl?: string;
    eventStreamDebug?: boolean;
    baseUrl?: string;
    basePath?: string;
};

export interface IAppSettings {
    overrideTheme?: ThemeName;
    client?: IClientSettings;
    palette: IPaletteSettings;
    icons: IIconSettings;
    debug: boolean;
    forgotPasswordUrl?: string;
    fontAwesomeParentPath: string;
};

export const AppSettings: IAppSettings = {
    client: {
        eventStreamUrl: import.meta.env.VITE_EVENT_STREAM_URL,
        baseUrl: import.meta.env.VITE_API_URL,
        basePath: '/api',
        eventStreamDebug: import.meta.env.DEV
    },
    palette: {
        primaryDark: '#7d6699',
        primaryLight: '#562c85',
        secondaryDark: '#99567e',
        secondaryLight: '#e8078c',
        GreenDark: "#3a5c37",
        GreenLight: "#9bbd9a",
        OrangeDark: "#79491d",
        OrangeLight: "#efb485",
        RedDark: "#74221f",
        RedLight: "#e19798"
        
    },
    icons: {
        alertSuccess: faCheckCircle,
        alertInformation: faInfoCircle,
        alertWarning: faExclamationTriangle,
        alertError: faTimesHexagon,
        alertDefault: faBan,
        favourite: solidStar,
        notFavourite: faStar,
        patients: faPerson,
        eCrf: faNotesMedical,
        stats: faInfo,
        screening: faFileLines,
        registration: faUser,
        drugManagement: faKitMedical,
        saeReview: faFileMedical,
        piSignOff: faPencil,
        medicalReview: faUserMagnifyingGlass,
        trialManagement: faFileLines,
        sourceDataVerification: faDatabase,
        trialParticipants: faPersonDress,
        faq: faQuestionCircle,
        training: faChalkboardUser,
        staffTraining: faGraduationCap,
        outstandingQueries: faChartColumn,
    },
    overrideTheme: overrideTheme,
    debug: import.meta.env.DEV,
    forgotPasswordUrl: (window as any).FORGOT_PASSWORD_URL ?? import.meta.env.VITE_FORGOT_PASSWORD_URL,
    fontAwesomeParentPath: (window as any).FONT_AWESOME_URL ?? import.meta.env.VITE_FONT_AWESOME_URL,

    sentry: {
        dsn: getSentryString('dsn', 'VITE_SENTRY_DSN'),
        debug: getSentryBoolean('debug', 'VITE_SENTRY_DEBUG') ?? false,
        enableMetrics: getSentryBoolean('enableMetrics', 'VITE_SENTRY_ENABLE_METRICS') ?? false,
        sendDefaultPii: getSentryBoolean('sendDefaultPii', 'VITE_SENTRY_SEND_DEFAULT_PII') ?? false,
        attachStacktrace: getSentryBoolean('attachStacktrace', 'VITE_SENTRY_ATTACH_STACKTRACE') ?? false,
        customer: getSentryString('customer', 'VITE_SENTRY_CUSTOMER') ?? 'Unknown',
        environment:  getSentryString('environment', 'VITE_SENTRY_ENVIRONMENT') ?? 'Development',
        application: getSentryString(undefined, 'VITE_SENTRY_APPLICATION') ?? 'SourceDataVerification',
        release: getSentryString(undefined, 'VITE_SENTRY_RELEASE'),
        enableLogs: getSentryBoolean('enableLogs', 'VITE_SENTRY_ENABLE_LOGS') ?? false,
        replaysOnErrorSampleRate: getSentryNumber('replaysOnErrorSampleRate', 'VITE_SENTRY_REPLAY_ON_ERROR_SAMPLE_RATE') ?? 1.0,
        replaysSessionSampleRate: getSentryNumber('replaysSessionSampleRate', 'VITE_SENTRY_REPLAY_SESSION_SAMPLE_RATE') ?? 0.1,
        tracesSampleRate: getSentryNumber('tracesSampleRate', 'VITE_SENTRY_TRACE_SAMPLE_RATE') ?? 0.2,
        enableReportABug: getSentryBoolean('enableReportABug', 'VITE_SENTRY_ENABLE_REPORT_A_BUG') ?? false
    }
};
